import { ReactElement } from 'react';
import { Trans, useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatters } from '@frontend/shared';
import { theme } from '@frontend/theme';
import { Text } from '@frontend/design-system';
import { nsrTimePeriods } from './filters';

export type ChangeProps = {
  diffFromLastPeriod?: number;
  timePeriod?: keyof typeof nsrTimePeriods;
};

export const BenchmarkComparisonText = ({ diffFromLastPeriod = 0, timePeriod }: ChangeProps) => {
  const { t } = useTranslation('analytics');

  let content: ReactElement | string = <></>;
  let showAlertIcon = false;

  let currentPeriod = '';
  let previousPeriod = '';

  switch (timePeriod) {
    case 'lastMonth':
      currentPeriod = t('the last month');
      previousPeriod = t('the previous month');
      break;

    case 'thisMonth':
      currentPeriod = t('this month');
      previousPeriod = t('the last month');
      break;

    case 'thisQuarter':
      currentPeriod = t('this quarter');
      previousPeriod = t('the previous quarter');
      break;

    default:
      currentPeriod = t('the selected period');
      previousPeriod = t('the previous period');
      break;
  }

  // Different from previous period
  if (!!diffFromLastPeriod) {
    const hasIncreased = diffFromLastPeriod > 0;
    const transValues = {
      diffFromLastPeriod: formatters.appendPercent(Math.abs(diffFromLastPeriod)),
    };
    showAlertIcon = hasIncreased;

    if (hasIncreased) {
      content = (
        <Trans t={t} values={transValues}>
          Your overall no-show rate for {{ currentPeriod }} has increased by{' '}
          <strong style={{ color: theme.colors.critical50 }}>
            {{ diffFromLastPeriod: transValues.diffFromLastPeriod }}
          </strong>{' '}
          compared to {{ previousPeriod }}
        </Trans>
      );
    } else {
      content = (
        <Trans t={t} values={transValues}>
          Your overall no-show rate for {{ currentPeriod }} has decreased by{' '}
          <strong>{{ diffFromLastPeriod: transValues.diffFromLastPeriod }}</strong> compared to {{ previousPeriod }}
        </Trans>
      );
    }
  }
  // Same as previous period
  else {
    showAlertIcon = false;
    content = (
      <Trans t={t}>
        Your overall no-show rate for {{ currentPeriod }} stayed the same as {{ previousPeriod }}
      </Trans>
    );
  }

  return (
    <div style={{ display: 'flex', gap: theme.spacing(1) }}>
      <Icon
        color={showAlertIcon ? 'error' : 'success'}
        name={showAlertIcon ? 'alert' : 'confirm-badge-small'}
        size={20}
        style={{ marginTop: theme.spacing(0.25) }}
      />
      <Text>{content}</Text>
    </div>
  );
};
